import * as React from 'react'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Avatar & PersonChip
 *
 * Display people consistently across the design system — tables, sidebars,
 * dropdowns, notifications, comments, and search results.
 *
 * Avatar: A circular image or initials fallback with brand colors.
 * PersonChip: Avatar + name + optional role badge in a single inline element.
 *
 * The initials fallback uses navy background with cream text by default,
 * gold for "highlighted" state, or maroon for "accent" state.
 *
 * Usage:
 *   // Avatar only
 *   <Avatar src="/photos/john.jpg" name="John Baker" />
 *   <Avatar name="Marie Cormier" size="sm" />
 *   <Avatar name="Luke Fontenot" size="lg" variant="gold" />
 *
 *   // PersonChip (avatar + name + role)
 *   <PersonChip name="Mrs. Dupre" role="Faculty" src="/photos/dupre.jpg" />
 *   <PersonChip name="John Baker" role="9th Grade" badge />
 */

// ─── Avatar ─────────────────────────────────────────────────────────────────

export type AvatarVariant = 'navy' | 'gold' | 'maroon'
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
	/** Image source URL */
	src?: string | null
	/** Full name (used for initials + alt text) */
	name: string
	/** Size preset */
	size?: AvatarSize
	/** Color variant for initials fallback */
	variant?: AvatarVariant
	/** Show a status dot */
	status?: 'online' | 'offline' | 'away' | 'busy'
}

const sizeStyles: Record<AvatarSize, string> = {
	xs: 'w-6 h-6 text-[9px]',
	sm: 'w-8 h-8 text-[10px]',
	md: 'w-10 h-10 text-xs',
	lg: 'w-12 h-12 text-sm',
	xl: 'w-16 h-16 text-base',
}

const variantStyles: Record<AvatarVariant, string> = {
	navy: 'bg-navy text-cream-50',
	gold: 'bg-gold text-navy-900',
	maroon: 'bg-maroon text-cream-50',
}

const statusStyles: Record<string, string> = {
	online: 'bg-success',
	offline: 'bg-muted-foreground/30',
	away: 'bg-warning',
	busy: 'bg-error',
}

function getInitials(name: string): string {
	const parts = name.trim().split(/\s+/)
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function Avatar({
	src,
	name,
	size = 'md',
	variant = 'navy',
	status,
	className,
	...props
}: AvatarProps) {
	const initials = getInitials(name)
	const showStatus = status !== undefined

	return (
		<span
			className={cn(
				'inline-flex items-center justify-center rounded-full overflow-hidden',
				'flex-shrink-0 font-ui font-semibold uppercase',
				sizeStyles[size],
				!src && variantStyles[variant],
				className,
			)}
			{...props}
		>
			{src ? (
				<img
					src={src}
					alt={name}
					className="w-full h-full object-cover"
					draggable={false}
				/>
			) : (
				<span aria-hidden="true">{initials}</span>
			)}

			{/* Status dot */}
			{showStatus && (
				<span
					className={cn(
						'absolute bottom-0 right-0 rounded-full border-2 border-card',
						statusStyles[status],
						size === 'xs' && 'w-1.5 h-1.5',
						(size === 'sm' || size === 'md') && 'w-2.5 h-2.5',
						(size === 'lg' || size === 'xl') && 'w-3 h-3',
					)}
				/>
			)}
		</span>
	)
}

// ─── PersonChip ─────────────────────────────────────────────────────────────

export interface PersonChipProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Image source */
	src?: string | null
	/** Full name */
	name: string
	/** Role / subtitle (e.g., "Faculty", "9th Grade") */
	role?: string
	/** Show role as a badge */
	badge?: boolean
	/** Badge variant */
	badgeVariant?: 'navy' | 'maroon' | 'gold' | 'cream' | 'success' | 'warning' | 'error' | 'info'
	/** Avatar size */
	size?: AvatarSize
	/** Avatar color variant */
	variant?: AvatarVariant
	/** Avatar status dot */
	status?: 'online' | 'offline' | 'away' | 'busy'
	/** Layout direction */
	horizontal?: boolean
}

const badgeColorStyles: Record<string, string> = {
	navy: 'bg-navy text-cream-50',
	maroon: 'bg-maroon text-cream-50',
	gold: 'bg-gold text-navy-900',
	cream: 'bg-cream text-navy-700 border border-cream-dark',
	success: 'bg-success/15 text-success',
	warning: 'bg-warning/15 text-warning',
	error: 'bg-error/15 text-error',
	info: 'bg-info/15 text-info',
}

export function PersonChip({
	src,
	name,
	role,
	badge = false,
	badgeVariant = 'navy',
	size = 'sm',
	variant = 'navy',
	status,
	horizontal = true,
	className,
	...props
}: PersonChipProps) {
	return (
		<div
			className={cn(
				'flex items-center gap-2',
				!horizontal && 'flex-col items-center text-center',
				className,
			)}
			{...props}
		>
			<Avatar src={src} name={name} size={size} variant={variant} status={status} />

			{horizontal ? (
				<div className="min-w-0 flex-1 flex items-center gap-1.5">
					<span className="text-sm font-medium font-ui truncate text-foreground">
						{name}
					</span>
					{role && (
						badge ? (
							<span
								className={cn(
									'inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold',
									'uppercase tracking-wider rounded-full leading-tight whitespace-nowrap',
									'font-ui',
									badgeColorStyles[badgeVariant],
								)}
							>
								{role}
							</span>
						) : (
							<span className="text-xs text-muted-foreground font-ui truncate">
								{role}
							</span>
						)
					)}
				</div>
			) : (
				<div className="flex flex-col">
					<span className="text-sm font-medium font-ui text-foreground">{name}</span>
					{role && (
						<span className="text-xs text-muted-foreground font-ui">{role}</span>
					)}
				</div>
			)}
		</div>
	)
}
